import { decryptObject, encryptObject, generateRandomNumber } from './CommonHelper';
interface AdminUser {
  token: string;
  // add other properties if needed
}
export const getUserInfo = () => {
  const userInfo = localStorage.getItem('userInfo') || sessionStorage.getItem('userInfo');

  if (userInfo) {
    return decryptObject(userInfo);
  }
  return null;
};

export const storeUserInfo = (obj: any): void => {
  const encryptedObj = encryptObject(obj);
  storeLocalStorage('userInfo', encryptedObj);
  if (obj.user_table_setting) {
    storeTableSettings(obj.user_table_setting);
  }
};
export const getUserAuthToken = (): string => {
  const userInfo = getUserInfo() as AdminUser;
  let authorization = '';
  if (userInfo) {
    authorization = userInfo.token;
  }
  return authorization;
};
export const storeSessionStorage = (key: string, value: string): void => {
  sessionStorage.setItem(key, value);
};
export const getSessionStorage = (key: string): string | null => {
  return sessionStorage.getItem(key);
};

export const storeLocalStorage = (key: string, value: string): void => {
  localStorage.setItem(key, value);
};

export const getLocalStorage = (key: string): string | null => {
  return localStorage.getItem(key) || sessionStorage.getItem(key);
};
export const removeLocalStorage = (key: string): void => {
  localStorage.removeItem(key);
  sessionStorage.removeItem(key);
};

const GUEST_DELIVERY_LOCATION_KEY = 'guestDeliveryLocation';

/**
 * Delivery location a guest picked off the FFL map. Guests have no address book to
 * read back from the API, so the choice is kept here to survive reloads and new tabs.
 */
export const storeGuestDeliveryLocation = (details: object): void => {
  storeLocalStorage(GUEST_DELIVERY_LOCATION_KEY, encryptObject(details));
};

/** Dropped on login — the account's own address book takes over from there. */
export const removeGuestDeliveryLocation = (): void => {
  removeLocalStorage(GUEST_DELIVERY_LOCATION_KEY);
};

/** Null when nothing is stored, or when the stored value can no longer be read. */
export const getGuestDeliveryLocation = (): any => {
  const stored = getLocalStorage(GUEST_DELIVERY_LOCATION_KEY);
  if (!stored) {
    return null;
  }
  try {
    return decryptObject(stored);
  } catch {
    removeLocalStorage(GUEST_DELIVERY_LOCATION_KEY);
    return null;
  }
};

const SELECTED_FFL_COUPONS_KEY = 'selectedFflCoupons';

const getSelectedFflCoupons = (): Record<string, string> => {
  const stored = getLocalStorage(SELECTED_FFL_COUPONS_KEY);
  if (!stored) {
    return {};
  }
  try {
    return JSON.parse(stored) || {};
  } catch {
    removeLocalStorage(SELECTED_FFL_COUPONS_KEY);
    return {};
  }
};

/**
 * Coupon a customer picked on a product page, kept per FFL so it is still applied when
 * that FFL's items reach checkout (straight via "Buy Now" or later through the cart).
 * Pass null to drop it.
 */
export const storeSelectedFflCoupon = (fflId: string, code: string | null): void => {
  if (!fflId) return;
  const coupons = getSelectedFflCoupons();
  if (code) {
    coupons[fflId] = code;
  } else {
    delete coupons[fflId];
  }
  storeLocalStorage(SELECTED_FFL_COUPONS_KEY, JSON.stringify(coupons));
};

/** Every coupon picked on a product page, as the { ffl_id, code } pairs the cart API quotes. */
export const getSelectedFflCouponList = (): { ffl_id: string; code: string }[] =>
  Object.entries(getSelectedFflCoupons()).map(([ffl_id, code]) => ({ ffl_id, code }));

export const getSelectedFflCoupon = (fflId?: string | null): string | null => {
  if (!fflId) return null;
  return getSelectedFflCoupons()[fflId] || null;
};

/** Dropped on login/logout — a pick made under one session must not carry over to the next. */
export const removeSelectedFflCoupons = (): void => {
  removeLocalStorage(SELECTED_FFL_COUPONS_KEY);
};

export const removeUserData = (): void => {
  removeSelectedFflCoupons();
  removeLocalStorage('userInfo');
  removeLocalStorage('deviceId');
  removeLocalStorage('tableSettingInfo');
};
export const getDeviceId = (): string => {
  let deviceId = getLocalStorage('deviceId');
  if (deviceId) {
    return deviceId;
  } else {
    deviceId = generateRandomNumber().toString();
    storeLocalStorage('deviceId', deviceId);
    return deviceId;
  }
};
export const getModuleTableSettings = (key?: string) => {
  let tableSettingInfo: any = localStorage.getItem('tableSettingInfo');
  if (tableSettingInfo) {
    tableSettingInfo = decryptObject(tableSettingInfo);
    if (key && tableSettingInfo[key]) {
      return tableSettingInfo[key];
    } else {
      return tableSettingInfo;
    }
  }
  return null;
};
export const storeModuleTableSettings = (key: string, details: any) => {
  const existingObj = getModuleTableSettings();
  const newObject = { ...existingObj, ...{ [key]: details } };
  storeTableSettings(newObject);
};
export const storeTableSettings = (details: any) => {
  storeLocalStorage('tableSettingInfo', encryptObject(details));
};
export const getUserCurrentRole = () => {
  const userInfo: any = getUserInfo();
  if (userInfo?.selected_role) {
    return userInfo.selected_role;
  }
  return null;
};
